import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  zipPromotionManagementService,
  ZipPromotion,
} from '../services/zipPromotionManagement';
import { Table, TableColumn } from '../components/Table';

export const ZipPromotionsTab: React.FC = () => {
  const [promotions, setPromotions] = useState<ZipPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<Record<string, unknown> | undefined>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<ZipPromotion | null>(null);
  const itemsPerPage = 50;
  const lastEvaluatedKeyRef = useRef<Record<string, unknown> | undefined>();
  lastEvaluatedKeyRef.current = lastEvaluatedKey;

  const loadPromotions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: { limit: number; lastEvaluatedKey?: Record<string, unknown> } = {
        limit: itemsPerPage,
      };
      if (currentPage > 1 && lastEvaluatedKeyRef.current) {
        params.lastEvaluatedKey = lastEvaluatedKeyRef.current;
      }
      const response = await zipPromotionManagementService.listZipPromotions(params);
      setPromotions(response.zipPromotions);
      setLastEvaluatedKey(response.lastEvaluatedKey);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      console.error('Error loading zip promotions:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const handleDelete = useCallback(async (promotionId: string) => {
    if (!window.confirm('Are you sure you want to delete this zip promotion?')) return;
    try {
      await zipPromotionManagementService.deleteZipPromotion(promotionId);
      await loadPromotions();
    } catch (err: unknown) {
      alert(getErrorMessage(err));
    }
  }, [loadPromotions]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatus = (p: ZipPromotion): string => {
    if (!p.isActive) return 'inactive';
    const now = new Date();
    if (now < new Date(p.startDate)) return 'upcoming';
    if (now > new Date(p.endDate)) return 'expired';
    return 'active';
  };

  const columns: TableColumn<ZipPromotion>[] = useMemo(
    () => [
      {
        header: 'Title',
        accessor: 'title',
        render: (v) => <div className="zip-promotion-title">{v as string}</div>,
      },
      {
        header: 'Zip Codes',
        accessor: (row) =>
          row.zipCodes?.length ? row.zipCodes.join(', ') : 'All',
        render: (v) => (
          <span className="zip-codes-badge" title={String(v)}>
            {String(v)}
          </span>
        ),
      },
      {
        header: 'Services Required',
        accessor: (row) => row.servicesRequired ?? 10,
      },
      {
        header: 'Service Types',
        accessor: (row) =>
          row.applicableServiceTypes?.length ? row.applicableServiceTypes.join(', ') : 'All',
        render: (v) => (
          <span className="zip-codes-badge" title={String(v)}>
            {String(v)}
          </span>
        ),
        hideOnTablet: true,
      },
      {
        header: 'Status',
        accessor: (row) => getStatus(row),
        render: (v) => (
          <span className={`status-badge ${v as string}`}>{v as string}</span>
        ),
      },
      {
        header: 'Start',
        accessor: 'startDate',
        render: (v) => formatDate(v as string),
        hideOnTablet: true,
      },
      {
        header: 'End',
        accessor: 'endDate',
        render: (v) => formatDate(v as string),
        hideOnMobile: true,
      },
      {
        header: 'Actions',
        accessor: (row) => (
          <div className="action-buttons">
            <button
              className="edit-button"
              onClick={() => setEditingPromotion(row)}
            >
              Edit
            </button>
            <button
              className="delete-button"
              onClick={() => handleDelete(row.promotionId)}
            >
              Delete
            </button>
          </div>
        ),
        dataLabel: '',
      },
    ],
    [handleDelete]
  );

  return (
    <div className="zip-promotions-tab">
      <div className="zip-promotions-tab-header">
        <button onClick={() => setShowCreateModal(true)} className="create-button">
          Create Zip Promotion
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading zip promotions...</div>
      ) : (
        <>
          <div className="zip-promotions-stats">
            <div className="stat-card">
              <div className="stat-value">{promotions.length}</div>
              <div className="stat-label">Zip Promotions</div>
            </div>
          </div>

          <Table
            columns={columns}
            data={promotions}
            getRowId={(row) => row.promotionId}
            emptyMessage="No zip promotions yet. Create one to enable use 10 get 1 free by zip code."
            containerClassName="zip-promotions-table-container"
            className="zip-promotions-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage((p) => p - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || promotions.length < itemsPerPage}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {showCreateModal && (
        <CreateZipPromotionModal
          onClose={async () => {
            setShowCreateModal(false);
            await loadPromotions();
          }}
        />
      )}

      {editingPromotion && (
        <EditZipPromotionModal
          promotion={editingPromotion}
          onClose={async () => {
            setEditingPromotion(null);
            await loadPromotions();
          }}
        />
      )}
    </div>
  );
};

const formatDateForInput = (dateString: string): string => {
  try {
    return new Date(dateString).toISOString().slice(0, 16);
  } catch {
    return dateString;
  }
};

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const res = (err as { response?: { data?: { error?: { message?: string } }; status?: number } }).response;
    if (res?.data?.error?.message) return res.data.error.message;
    if (res?.status === 401) return 'Session expired. Please log in again.';
    if (res?.status === 403) return 'Access denied.';
  }
  return err instanceof Error ? err.message : 'An unexpected error occurred';
}

const CreateZipPromotionModal: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    zipCodes: '',
    servicesRequired: '10',
    applicableServiceTypes: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await zipPromotionManagementService.createZipPromotion({
        title: formData.title,
        description: formData.description || undefined,
        promotionType: 'use_10_get_1_free',
        zipCodes: formData.zipCodes
          ? formData.zipCodes.split(/[\s,]+/).filter(Boolean)
          : [],
        servicesRequired: parseInt(formData.servicesRequired, 10) || 10,
        rewardType: 'free_service',
        startDate: formData.startDate,
        endDate: formData.endDate,
        isActive: formData.isActive,
        applicableServiceTypes: formData.applicableServiceTypes
          ? formData.applicableServiceTypes.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
          : undefined,
      });
      onClose();
    } catch (err: unknown) {
      alert(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create Zip Promotion</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Zip Codes (comma or space separated, leave empty for all)</label>
            <input
              type="text"
              value={formData.zipCodes}
              onChange={(e) =>
                setFormData({ ...formData, zipCodes: e.target.value })
              }
              placeholder="400001, 400002, 400003"
            />
          </div>
          <div className="form-group">
            <label>Services Required (e.g. 10 for use 10 get 1 free)</label>
            <input
              type="number"
              min="1"
              value={formData.servicesRequired}
              onChange={(e) =>
                setFormData({ ...formData, servicesRequired: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Applicable Service Types (comma or space separated, empty for all)</label>
            <input
              type="text"
              value={formData.applicableServiceTypes}
              onChange={(e) =>
                setFormData({ ...formData, applicableServiceTypes: e.target.value })
              }
              placeholder="home_cleaning, plumbing, electrical"
            />
          </div>
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="datetime-local"
              value={formData.startDate}
              onChange={(e) =>
                setFormData({ ...formData, startDate: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>End Date *</label>
            <input
              type="datetime-local"
              value={formData.endDate}
              onChange={(e) =>
                setFormData({ ...formData, endDate: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditZipPromotionModal: React.FC<{
  promotion: ZipPromotion;
  onClose: () => void;
}> = ({ promotion, onClose }) => {
  const [formData, setFormData] = useState({
    title: promotion.title,
    description: promotion.description || '',
    zipCodes: promotion.zipCodes?.join(', ') || '',
    servicesRequired: String(promotion.servicesRequired ?? 10),
    applicableServiceTypes: promotion.applicableServiceTypes?.join(', ') || '',
    startDate: formatDateForInput(promotion.startDate),
    endDate: formatDateForInput(promotion.endDate),
    isActive: promotion.isActive,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await zipPromotionManagementService.updateZipPromotion(
        promotion.promotionId,
        {
          title: formData.title,
          description: formData.description || undefined,
          zipCodes: formData.zipCodes
            ? formData.zipCodes.split(/[\s,]+/).filter(Boolean)
            : [],
          servicesRequired: parseInt(formData.servicesRequired, 10) || 10,
          startDate: formData.startDate,
          endDate: formData.endDate,
          isActive: formData.isActive,
          applicableServiceTypes: formData.applicableServiceTypes
            ? formData.applicableServiceTypes.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
            : undefined,
        }
      );
      onClose();
    } catch (err: unknown) {
      alert(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Edit Zip Promotion</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Zip Codes (comma or space separated, empty for all)</label>
            <input
              type="text"
              value={formData.zipCodes}
              onChange={(e) =>
                setFormData({ ...formData, zipCodes: e.target.value })
              }
              placeholder="400001, 400002"
            />
          </div>
          <div className="form-group">
            <label>Services Required</label>
            <input
              type="number"
              min="1"
              value={formData.servicesRequired}
              onChange={(e) =>
                setFormData({ ...formData, servicesRequired: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Applicable Service Types (comma or space separated, empty for all)</label>
            <input
              type="text"
              value={formData.applicableServiceTypes}
              onChange={(e) =>
                setFormData({ ...formData, applicableServiceTypes: e.target.value })
              }
              placeholder="home_cleaning, plumbing, electrical"
            />
          </div>
          <div className="form-group">
            <label>Start Date *</label>
            <input
              type="datetime-local"
              value={formData.startDate}
              onChange={(e) =>
                setFormData({ ...formData, startDate: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>End Date *</label>
            <input
              type="datetime-local"
              value={formData.endDate}
              onChange={(e) =>
                setFormData({ ...formData, endDate: e.target.value })
              }
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
              Active
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
